import { motion } from 'framer-motion';
import { ShoppingBag, Heart, Star, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useState } from 'react';

const Tienda = () => {
  const [categoriaActiva, setCategoriaActiva] = useState('todos');

  const categorias = [
    { id: 'todos', label: 'Todos' },
    { id: 'cartas', label: 'Cartas Astrales' },
    { id: 'joyas', label: 'Joyas' },
    { id: 'libros', label: 'Libros' },
    { id: 'rituales', label: 'Rituales' },
  ];

  const productos = [
    {
      id: 1,
      nombre: 'Carta Astral Completa',
      descripcion: 'Análisis detallado de tu carta natal con interpretación personalizada de 30 páginas.',
      precio: 89,
      categoria: 'cartas',
      imagen: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f5?w=600&q=80',
      rating: 5.0,
      reviews: 127,
      badge: 'Más Vendido',
    },
    {
      id: 2,
      nombre: 'Collar Zodiacal Plata',
      descripcion: 'Collar con tu signo zodiacal en plata 925 con cadena ajustable.',
      precio: 45,
      categoria: 'joyas',
      imagen: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=600&q=80',
      rating: 4.8,
      reviews: 89,
      badge: null,
    },
    {
      id: 3,
      nombre: 'Guía de Astrología 2024',
      descripcion: 'Libro completo con predicciones anuales para todos los signos.',
      precio: 29,
      categoria: 'libros',
      imagen: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&q=80',
      rating: 4.9,
      reviews: 234,
      badge: 'Nuevo',
    },
    {
      id: 4,
      nombre: 'Kit de Limpieza Energética',
      descripcion: 'Incluye salvia blanca, palo santo, cristales y guía de uso.',
      precio: 35,
      categoria: 'rituales',
      imagen: 'https://images.unsplash.com/photo-1603539278420-b31764f3d8c7?w=600&q=80',
      rating: 4.7,
      reviews: 156,
      badge: null,
    },
    {
      id: 5,
      nombre: 'Revolución Solar Personalizada',
      descripcion: 'Predicciones detalladas para tu año astral con fechas clave.',
      precio: 59,
      categoria: 'cartas',
      imagen: 'https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=600&q=80',
      rating: 4.9,
      reviews: 98,
      badge: null,
    },
    {
      id: 6,
      nombre: 'Pulsera de Chakras',
      descripcion: 'Pulsera con piedras naturales para equilibrar tus chakras.',
      precio: 28,
      categoria: 'joyas',
      imagen: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=600&q=80',
      rating: 4.6,
      reviews: 67,
      badge: null,
    },
    {
      id: 7,
      nombre: 'Tarot Rider-Waite',
      descripcion: 'Barcla clásica de tarot con guía de interpretación incluida.',
      precio: 32,
      categoria: 'libros',
      imagen: 'https://images.unsplash.com/photo-1636955691605-6e0082299752?w=600&q=80',
      rating: 4.8,
      reviews: 312,
      badge: 'Clásico',
    },
    {
      id: 8,
      nombre: 'Set de Cristales Astrológicos',
      descripcion: '12 cristales seleccionados según tu carta natal personalizada.',
      precio: 75,
      categoria: 'rituales',
      imagen: 'https://images.unsplash.com/photo-1612817288484-6f916006741a?w=600&q=80',
      rating: 4.9,
      reviews: 45,
      badge: 'Edición Limitada',
    },
  ];

  const productosFiltrados = categoriaActiva === 'todos' 
    ? productos 
    : productos.filter(p => p.categoria === categoriaActiva);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="py-24 px-4 sm:px-6 lg:px-8 bg-purple-950/30">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Badge className="mb-4 bg-purple-500/20 text-purple-200 border-purple-400/30">
            <ShoppingBag className="w-4 h-4 mr-2" />
            Tienda Esotérica
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Productos Místicos
          </h2>
          <p className="text-lg text-purple-100/60 max-w-2xl mx-auto">
            Descubre nuestra colección de productos seleccionados para tu práctica espiritual 
            y crecimiento personal.
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          className="flex flex-col md:flex-row gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-purple-400" />
            <Input
              placeholder="Buscar productos..."
              className="pl-10 bg-purple-950/50 border-purple-500/30 text-purple-100 placeholder:text-purple-400/50"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
            <Filter className="w-5 h-5 text-purple-400 flex-shrink-0" />
            {categorias.map((cat) => (
              <Button
                key={cat.id}
                variant={categoriaActiva === cat.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategoriaActiva(cat.id)}
                className={`flex-shrink-0 ${
                  categoriaActiva === cat.id
                    ? 'bg-purple-600 text-white'
                    : 'border-purple-400/30 text-purple-200 hover:bg-purple-500/10'
                }`}
              >
                {cat.label}
              </Button>
            ))}
          </div>
        </motion.div>

        {/* Products Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {productosFiltrados.map((producto) => (
            <motion.div key={producto.id} variants={itemVariants}>
              <Card className="group h-full bg-purple-950/40 border-purple-500/20 overflow-hidden hover:border-purple-400/40 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                {/* Imagen */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={producto.imagen}
                    alt={producto.nombre}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-950/60 to-transparent" />
                  
                  {producto.badge && (
                    <Badge className="absolute top-3 left-3 bg-amber-500/90 text-white border-0">
                      {producto.badge}
                    </Badge>
                  )}
                  
                  <motion.button
                    className="absolute top-3 right-3 p-2 rounded-full bg-purple-950/80 text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Heart className="w-4 h-4" />
                  </motion.button>

                  {/* Quick add */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-purple-950 to-transparent translate-y-full group-hover:translate-y-0 transition-transform duration-300"
                  >
                    <Button
                      size="sm"
                      className="w-full bg-purple-600 hover:bg-purple-500 text-white"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Añadir al Carrito
                    </Button>
                  </motion.div>
                </div>

                <CardContent className="p-4">
                  <h3 className="font-semibold text-purple-100 mb-1 group-hover:text-purple-300 transition-colors">
                    {producto.nombre}
                  </h3>
                  <p className="text-sm text-purple-100/50 line-clamp-2 mb-3">
                    {producto.descripcion}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      <span className="text-sm text-purple-200">{producto.rating}</span>
                      <span className="text-sm text-purple-400/50">({producto.reviews})</span>
                    </div>
                    <span className="text-lg font-bold text-purple-300">${producto.precio}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* View All */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Button
            variant="outline"
            size="lg"
            className="border-purple-400/30 text-purple-200 hover:bg-purple-500/10"
          >
            Ver Todos los Productos
            <ShoppingBag className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default Tienda;
